import { useCallback } from 'react';
import useApi from '../../Api';
import { useAuth } from '../../AuthContext';

export const useSyncItinerary = (stateRef) => {
    const apiClient = useApi();
    const { token } = useAuth();

    function formatDate(date) {
        if (!date) return null;
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    function formatTime(time) {
        if (!time) return null;
        return `${String(time.getHours()).padStart(2, '0')}:${String(time.getMinutes()).padStart(2, '0')}:${String(time.getSeconds()).padStart(2, '0')}`;
    }

    const syncColumns = useCallback(async () => {
        const selectedItinerary = stateRef.current.selectedItinerary;

        if (!selectedItinerary) {
            return;
        }

        if (!selectedItinerary.columns || selectedItinerary.columns.length === 0) {
            return;
        }

        const columnsPayload = selectedItinerary.columns.reduce((acc, column, index) => {
            const column_id = column.id;
            acc[column_id] = {
                name: column.title,
                position: index,
                visibility: selectedItinerary.columnVisibility[column_id],
                color: selectedItinerary.columnMarkerColors[column_id],
                date: formatDate(column.date)
            };
            return acc;
        }, {});

        try {
            const response = await apiClient.put(`/itineraries/${selectedItinerary.id}/sync_columns`, {'columns': columnsPayload}, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.status !== 200) {
                throw new Error('Failed to sync columns');
            }

            // console.log('Columns synced successfully');
        } catch (error) {
            console.error('Error syncing columns:', error);
            throw error;
        }
    }, [apiClient, token, stateRef]);

    const syncSavedPlacesColumn = useCallback(async () => {
        const selectedItinerary = stateRef.current.selectedItinerary;

        if (!selectedItinerary) {
            return;
        }

        const savedPlacesData = {
            visibility: selectedItinerary.columnVisibility['saved-places'],
            color: selectedItinerary.columnMarkerColors['saved-places'],
        };

        try {
            const response = await apiClient.put(`/itineraries/${selectedItinerary.id}/sync_saved_places_column`, savedPlacesData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.status !== 200) {
                throw new Error('Failed to sync saved places');
            }

            // console.log('Saved places column synced successfully');
        } catch (error) {
            console.error('Error syncing saved places:', error);
            throw error;
        }
    }, [apiClient, token, stateRef]);

    const syncSavedPlacesList = useCallback(async () => {
        const selectedItinerary = stateRef.current.selectedItinerary;

        if (!selectedItinerary) {
          return;
        }

        const savedPlaces = stateRef.current.savedPlaces[selectedItinerary.id];

        if (!savedPlaces || savedPlaces.length === 0) {
            return;
        }

        const placesPayload = {
            places: savedPlaces.map((place, index) => ({
                place_id: place.place_id || null,
                google_place_id: place.google_place_id || null,
                position: index,
                date: place.date ? formatDate(place.date) : null,
                time: place.time ? formatTime(place.time) : null,
                notes: place.notes || null,
            })),
        };

        try {
            const response = await apiClient.put(`/itineraries/${selectedItinerary.id}/sync_saved_places_list`, placesPayload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.status !== 200) {
                throw new Error('Failed to sync saved places');
            }

            // console.log('Saved places synced successfully');
        } catch (error) {
            console.error('Error syncing saved places:', error);
            throw error;
        }
    }, [apiClient, token, stateRef]);

    const syncColumnPlaces = useCallback(async () => {
        const selectedItinerary = stateRef.current.selectedItinerary;

        if (!selectedItinerary) {
            return;
        }

        if (!selectedItinerary.columns || selectedItinerary.columns.length === 0) {
            return;
        }

        const columns = selectedItinerary.columns.map((column, columnIndex) => {
            return {
                column_id: column.id,
                position: columnIndex,
                places: column.items.map((place, placeIndex) => ({
                    place_id: place.place_id || null,
                    google_place_id: place.google_place_id || null,
                    position: placeIndex,
                    date: place.date ? formatDate(place.date) : null,
                    time: place.time ? formatTime(place.time) : null,
                    notes: place.notes || null,
                }))
            };
        });

        const payload = { columns: columns };

        try {
            const response = await apiClient.put(`/itineraries/${selectedItinerary.id}/sync_column_places`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.status !== 200) {
                throw new Error('Failed to sync column places');
            }

            // console.log('Column places synced successfully');
        } catch (error) {
            console.error('Error syncing column places:', error);
            throw error;
        }
      }, [apiClient, token, stateRef]);

    const syncItineraryInfo = useCallback(async () => {
        const selectedItinerary = stateRef.current.selectedItinerary;

        if (!selectedItinerary) {
            return;
        }

        const itineraryPayload = {
            name: selectedItinerary.name,
            itinerary_origin: selectedItinerary.origin,
            itinerary_origin_id: selectedItinerary.origin_id,
            is_public: selectedItinerary.is_public,
        };

        try {
            const response = await apiClient.put(`/itineraries/${selectedItinerary.id}/sync_info`, itineraryPayload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.status !== 200) {
                throw new Error('Failed to sync itinerary info');
            }

            // console.log('Itinerary info synced successfully');
        } catch (error) {
            console.error('Error syncing itinerary info:', error);
            throw error;
        }
    }, [apiClient, token, stateRef]);

    return {
        syncColumns,
        syncSavedPlacesColumn,
        syncSavedPlacesList,
        syncColumnPlaces,
        syncItineraryInfo,
    };
};
